import React, { useState, useEffect } from 'react';
import { X, Save, Bot, Sparkles, MessageSquare, Send, Loader2, RefreshCw } from 'lucide-react';
import { Button, Badge } from '@/components/ui';
import { cn } from '@/utils/cn';
import { AIPersona, AIPersonaPayload } from '../types/persona.type';
import { usePersonas } from '../hooks/usePersonas';
import { useAuth } from '@/core/auth/AuthContext';
import { useAI } from '@/core/ai/AIContext';
import { useIntegrations } from '../../integrations/list/hooks/useIntegrations';
import { PRESENTATION_PROTOCOL } from '@/core/ai/ai.service';
import { MarkdownText } from '@/components/ui/MarkdownText';
import { ChatInput } from '@/components/ui/ChatInput';
import { toast } from 'sonner';

interface TrainingSandboxDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  persona: AIPersona | null;
}

export function TrainingSandboxDrawer({ isOpen, onClose, persona }: TrainingSandboxDrawerProps) {
  const { updatePersona, createPersona, resetPersona, isMutating } = usePersonas();
  const { profile } = useAuth();
  const { activeAI } = useAI();
  const { integrations } = useIntegrations();
  
  const [formData, setFormData] = useState<Partial<AIPersonaPayload>>({
    name: '',
    code: '',
    instruction: '',
    icon: 'ri-robot-line',
    is_active: true
  });

  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState<{role: 'user'|'assistant', content: string}[]>([]);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (persona) {
        setFormData({
          name: persona.name,
          code: persona.code,
          instruction: persona.instruction,
          icon: persona.icon || 'ri-robot-line',
          is_active: persona.is_active
        });
        setChatHistory([{ role: 'assistant', content: `Xin chào! Em là ${persona.name}. Sếp muốn em test thử kịch bản nào ạ?` }]);
      } else {
        setFormData({
          name: '',
          code: '',
          instruction: 'Luôn xưng hô chuyên nghiệp. Hãy trả lời ngắn gọn và đi thẳng vào vấn đề.',
          icon: 'ri-robot-line',
          is_active: true
        });
        setChatHistory([{ role: 'assistant', content: `Hệ thống đã sẵn sàng. Hãy thiết lập tính cách bên trái và lưu lại để test thử với em nhé!` }]);
      }
    }
  }, [isOpen, persona]);

  const handleSave = async () => {
    if (!formData.name || !formData.code || !formData.instruction) {
      toast.error('Vui lòng nhập đủ Tên, Mã định danh và Lời dặn dò!');
      return;
    }
    
    try {
      if (persona) {
        await updatePersona({ id: persona.id, payload: formData });
      } else {
        await createPersona(formData as AIPersonaPayload);
      }
      // Không đóng Drawer để user tiếp tục test
      toast.success('Đã nạp tính cách mới. Sếp có thể chat thử ngay ở bên phải!');
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleSimulateChat = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTyping(true);

    const geminiIntegration = integrations.find((i: any) => i.id === 'gemini');
    const apiKey = geminiIntegration?.config?.api_key || activeAI?.config?.api_key;
    
    let selectedModel = geminiIntegration?.config?.config?.selectedModel || activeAI?.config?.config?.selectedModel || 'gemini-2.5-flash';
    // Đảm bảo chỉ dùng các model hợp lệ hiện tại (2.5 flash, pro)
    if (!['gemini-2.5-flash', 'gemini-pro'].includes(selectedModel)) {
      selectedModel = 'gemini-2.5-flash';
    }
    
    if (!apiKey) {
      setTimeout(() => {
        setChatHistory(prev => [...prev, { role: 'assistant', content: `⚠️ Chưa có API Key! Sếp hãy quay lại tab "API Keys (Nền tảng)" để thiết lập kết nối trước nhé.` }]);
        setIsTyping(false);
      }, 500);
      return;
    }

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${selectedModel}:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system_instruction: {
            parts: [{ text: `Bạn là ${formData.name}. ${formData.instruction}\n\n${PRESENTATION_PROTOCOL}\nChỉ xưng hô theo luật được giao.` }]
          },
          contents: [
            ...chatHistory.slice(1).map(msg => ({ role: msg.role === 'user' ? 'user' : 'model', parts: [{ text: msg.content }] })),
            { role: 'user', parts: [{ text: userMessage }] }
          ]
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error?.message || `HTTP Error ${response.status}`);
      }

      const reply = data.candidates?.[0]?.content?.parts?.[0]?.text || "Không nhận được phản hồi từ AI.";

      setChatHistory(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (e: any) {
      setChatHistory(prev => [...prev, { role: 'assistant', content: `❌ Lỗi kết nối API: ${e.message}` }]);
    } finally {
      setIsTyping(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="w-full max-w-5xl bg-slate-50 h-full shadow-2xl flex flex-col animate-in slide-in-from-right-8 duration-500 rounded-l-3xl overflow-hidden">
        
        {/* Header */}
        <div className="h-16 px-6 bg-white border-b border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center">
              <Bot size={20} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 leading-tight">
                Xưởng Đào tạo Đặc vụ (AI Sandbox)
              </h2>
              <p className="text-[11px] text-slate-500 font-medium">Dạy AI bằng ngôn ngữ tự nhiên, không cần code.</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Split */}
        <div className="flex-1 flex overflow-hidden">
          
          {/* LEFT: Configuration */}
          <div className="w-1/2 border-r border-slate-200 bg-white overflow-y-auto p-6 space-y-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-emerald-700 font-black text-sm uppercase tracking-widest">
                <Sparkles size={16} />
                Định hình Nhân cách
              </div>
              {persona && (
                <button 
                  onClick={async () => {
                    if (window.confirm('Khôi phục Đặc vụ này về nội dung mặc định ban đầu của Landscape?')) {
                      try {
                        await resetPersona(persona);
                        onClose(); // Đóng Drawer sau khi khôi phục thành công để load lại
                      } catch (e) {}
                    }
                  }}
                  disabled={isMutating}
                  className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-amber-600 transition-colors"
                >
                  <RefreshCw size={12} className={cn(isMutating && "animate-spin")} />
                  Khôi phục gốc
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Tên Đặc vụ</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-emerald-500 outline-none"
                    placeholder="VD: Kế toán trưởng..."
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Mã Hệ thống</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold uppercase focus:ring-2 focus:ring-emerald-500 outline-none"
                    placeholder="VD: ACCOUNTANT"
                    value={formData.code}
                    disabled={!!persona}
                    onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex justify-between items-center">
                  Dặn dò (Lời nói tự nhiên)
                  <Badge className="bg-amber-100 text-amber-700 font-bold text-[9px] uppercase px-2 py-0.5 rounded-full">
                    AI Sẽ tự dịch sang Prompt
                  </Badge>
                </label>
                <div className="relative">
                  <textarea 
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm leading-relaxed min-h-[200px] focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
                    placeholder="Hãy dặn dò nhân viên của bạn. Ví dụ:
- Lúc báo giá luôn phải cộng 10% VAT.
- Ai hỏi việc riêng thì từ chối lịch sự.
- Giọng điệu vui vẻ, hay dùng icon cây cỏ..."
                    value={formData.instruction}
                    onChange={e => setFormData({ ...formData, instruction: e.target.value })}
                  />
                  <div className="absolute bottom-3 right-3 text-emerald-500 animate-pulse">
                    <Bot size={20} />
                  </div>
                </div>
                <p className="text-xs text-slate-400 italic font-medium leading-relaxed mt-2">
                  Chỉ cần gõ như bạn đang giao việc cho nhân viên. Dữ liệu này sẽ được nhồi trực tiếp vào System Instruction của mô hình AI.
                </p>
              </div>
            </div>

            <Button 
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl shadow-lg gap-2 mt-4"
              onClick={handleSave}
              disabled={isMutating}
            >
              {isMutating ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
              Lưu & Cập nhật Tính cách
            </Button>
          </div>

          {/* RIGHT: Live Sandbox Chat */}
          <div className="w-1/2 bg-slate-50 flex flex-col relative">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-100/40 via-transparent to-transparent pointer-events-none" />
            
            <div className="p-4 border-b border-slate-200 bg-white/50 backdrop-blur-md flex items-center justify-between z-10">
              <div className="flex items-center gap-2 text-slate-700 font-bold text-sm">
                <MessageSquare size={16} className="text-emerald-500" />
                Live Test Môi trường thực
              </div>
              <Badge className="bg-rose-100 text-rose-700 font-bold">Khu vực Giả lập</Badge>
            </div>

            {/* Chat History */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 z-10">
              {chatHistory.map((msg, idx) => (
                <div key={idx} className={cn("flex", msg.role === 'user' ? "justify-end" : "justify-start")}>
                  <div className={cn(
                    "max-w-[85%] rounded-2xl p-4 text-sm leading-relaxed shadow-sm",
                    msg.role === 'user' 
                      ? "bg-emerald-600 text-white rounded-br-none" 
                      : "bg-white border border-slate-200 text-slate-700 rounded-bl-none"
                  )}>
                    {msg.role === 'assistant' ? <MarkdownText content={msg.content} /> : msg.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-none px-5 py-4 shadow-sm flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="p-4 bg-white border-t border-slate-200 z-10">
              <ChatInput 
                value={chatInput}
                onChange={setChatInput}
                onSend={handleSimulateChat}
                placeholder="Chat thử để kiểm tra xem nó có nghe lời không..."
                disabled={isTyping}
              />
            </div>

          </div>

        </div>
      </div>
    </div>
  );
}
